/**
 * authApi - HTTP client for the Django OTP + JWT auth endpoints.
 * Token pair is persisted in localStorage under "chauvin.auth".
 */
import { getApiBaseUrl } from "./config";
import { completionDraftStorage } from "./completionDraftStorage";

export const AUTH_STORAGE_KEY = "chauvin.auth";

/** Custom event dispatched whenever clearSession() is called. */
export const AUTH_CLEARED_EVENT = "chauvin:auth-cleared";

/** Refresh the access token proactively when it expires within this window. */
export const TOKEN_REFRESH_MARGIN_MS = 2 * 60 * 1000;

/** Error message thrown when a request is attempted without a valid session. */
export const SESSION_EXPIRED_MESSAGE = "Session expirée, veuillez vous reconnecter";

/** Web Lock that serializes token refreshes across tabs. */
const REFRESH_LOCK_NAME = "chauvin.auth.refresh";
/** Longest a refresh may hold that lock before it fails like any refresh. */
export const REFRESH_TIMEOUT_MS = 20 * 1000;

/** Raised when a request cannot be authenticated; the sign-in flow takes over. */
export class SessionExpiredError extends Error {
  readonly status = 401;

  constructor() {
    super(SESSION_EXPIRED_MESSAGE);
    this.name = "SessionExpiredError";
  }
}

export function isSessionExpiredError(error: unknown): error is SessionExpiredError {
  return error instanceof SessionExpiredError;
}

export interface AppUser {
  id: string;
  phone: string;
  display_name: string;
  role: string;
  location: string;
  /** True if the user has at least one direct report (computed at login). */
  is_manager?: boolean;
}

export interface TokenPair {
  access: string;
  refresh: string;
  expiresAt: number; // epoch ms
}

export interface AuthSession {
  /** Stable across refreshes, unique to each sign-in. */
  sessionId?: string;
  tokens: TokenPair;
  user: AppUser;
}

// ---------------------------------------------------------------------------
// JWT helpers
// ---------------------------------------------------------------------------

/**
 * Decode the `exp` claim from a JWT access token and return it as epoch ms.
 * Falls back to Date.now() + 55 min if the token is malformed.
 */
function decodeJwtExp(token: string): number {
  try {
    const payload = token.split(".")[1];
    if (!payload) throw new Error("no payload");
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    if (typeof decoded.exp === "number") {
      return decoded.exp * 1000; // convert seconds -> ms
    }
  } catch {
    // fall through to default
  }
  return Date.now() + 55 * 60 * 1000;
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

export function loadSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as AuthSession;
    if (!session || typeof session !== "object" ||
      typeof session.user?.id !== "string" || !session.user.id ||
      !session.tokens || typeof session.tokens !== "object") return null;
    // Upgrade persisted sessions without forcing everyone to sign in again.
    if (typeof session.sessionId !== "string" || !session.sessionId) {
      session.sessionId = crypto.randomUUID();
      saveSession(session);
    }
    return session;
  } catch {
    return null;
  }
}

export function isSameSession(
  expected: AuthSession,
  current: AuthSession | null = loadSession(),
): boolean {
  return !!current && current.sessionId === expected.sessionId &&
    current.user?.id === expected.user?.id;
}

function saveSession(session: AuthSession): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  const ownerId = loadSession()?.user?.id;
  localStorage.removeItem(AUTH_STORAGE_KEY);
  if (ownerId) void completionDraftStorage.clearOwner(ownerId).catch(() => undefined);
  // Notify listeners in the current tab immediately.
  window.dispatchEvent(new CustomEvent(AUTH_CLEARED_EVENT));
}

// ---------------------------------------------------------------------------
// Core fetch helpers
// ---------------------------------------------------------------------------

async function post<T>(path: string, body: unknown, token?: string, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${getApiBaseUrl()}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Propagate the server error message so AuthContext can show it
    const msg =
      (data as Record<string, string>).error ??
      (data as Record<string, string>).detail ??
      `Erreur ${res.status}`;
    throw Object.assign(new Error(msg), { code: (data as Record<string, string>).code, status: res.status });
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Auth API
// ---------------------------------------------------------------------------

export async function requestOtp(phone: string): Promise<void> {
  await post("/auth/request-otp/", { phone });
}

export async function verifyOtp(
  phone: string,
  code: string
): Promise<AuthSession> {
  const data = await post<{ access: string; refresh: string; user: AppUser }>(
    "/auth/verify-otp/",
    { phone, code }
  );

  const session: AuthSession = {
    sessionId: crypto.randomUUID(),
    tokens: {
      access: data.access,
      refresh: data.refresh,
      expiresAt: decodeJwtExp(data.access),
    },
    user: data.user,
  };
  saveSession(session);
  return session;
}

export function holdsTokens(session: AuthSession | null, tokens: TokenPair): boolean {
  return session?.tokens?.access === tokens.access &&
    session.tokens.refresh === tokens.refresh;
}

/** Unexpired tokens stored for the same sign-in by another tab or caller. */
function rotatedTokens(started: AuthSession): TokenPair | null {
  const current = loadSession();
  if (!current || !isSameSession(started, current) || holdsTokens(current, started.tokens)) {
    return null;
  }
  const { access, expiresAt } = current.tokens;
  return typeof access === "string" && access && Date.now() < expiresAt ? current.tokens : null;
}

/**
 * Tabs share one rotating refresh token, so a tab that refreshes in parallel
 * would replay a blacklisted token. Without Web Locks (insecure contexts, old
 * browsers) a losing tab adopts the winner's tokens through rotatedTokens()
 * only if they are stored before its own rejection arrives; otherwise it
 * signs out as before.
 */
function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = globalThis.navigator?.locks;
  return locks ? locks.request(REFRESH_LOCK_NAME, task) : task();
}

async function requestRefresh(owner: AuthSession): Promise<TokenPair | null> {
  const stillOwnsTokens = () => {
    const current = loadSession();
    return isSameSession(owner, current) && holdsTokens(current, owner.tokens);
  };

  // A hung refresh would hold the cross-tab lock; fail it like any refresh.
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), REFRESH_TIMEOUT_MS);
  try {
    const data = await post<{ access: string; refresh: string }>(
      "/auth/refresh/",
      { refresh: owner.tokens.refresh },
      undefined,
      timeout.signal,
    );
    if (typeof data.access !== "string" || !data.access ||
      typeof data.refresh !== "string" || !data.refresh) {
      throw new Error("Invalid refresh response");
    }
    if (!stillOwnsTokens()) return rotatedTokens(owner);

    const updated: AuthSession = {
      ...owner,
      tokens: {
        access: data.access,
        refresh: data.refresh,
        expiresAt: decodeJwtExp(data.access),
      },
    };
    saveSession(updated);
    return updated.tokens;
  } catch {
    // A rejection after another tab rotated the tokens is not a sign-out.
    if (!stillOwnsTokens()) return rotatedTokens(owner);
    clearSession();
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// Single-flight refresh: concurrent callers share the same in-flight promise
// so only one /auth/refresh/ request is ever in flight at a time.
// This prevents refresh-token stampede when ROTATE_REFRESH_TOKENS=True.
let _refresh: { session: AuthSession; promise: Promise<TokenPair | null> } | null = null;

export function refreshAccessToken(): Promise<TokenPair | null> {
  const session = loadSession();
  if (!session) return Promise.resolve(null);
  if (_refresh && isSameSession(_refresh.session, session)) return _refresh.promise;

  const promise = withRefreshLock(async (): Promise<TokenPair | null> => {
    // Another tab may have rotated the tokens while this one waited.
    const current = loadSession();
    if (!current || !isSameSession(session, current)) return null;
    return rotatedTokens(session) ?? requestRefresh(current);
  }).finally(() => {
    if (_refresh?.promise === promise) _refresh = null;
  });

  _refresh = { session, promise };
  return promise;
}

export async function logout(refreshToken: string): Promise<void> {
  const session = loadSession();
  // A caller may have awaited device cleanup after capturing its refresh token.
  if (!session || session.tokens.refresh !== refreshToken) return;
  try {
    await post("/auth/logout/", { refresh: refreshToken }, session?.tokens.access);
  } finally {
    if (session && isSameSession(session)) clearSession();
  }
}
